import { useCallback, useRef } from 'react';
import { randomUUID } from 'utils/uuid';
import {
  trackVideoFeedItemView,
  trackClick,
} from 'services/snowplowAnalytics/snowplowAnalytics';
import { useSnowplow } from 'contexts/SnowplowContext';
import { useCookieConsent } from 'contexts/CookieConsentContext/CookieConsentContext';
import type { OnItemVisible, OnItemClick } from '@yleisradio/lyhyet-web-items';

export const useVideoFeedTracking = (
  contentIndex: number,
  title: string | undefined
) => {
  const { isSnowplowReady } = useSnowplow();
  const consent = useCookieConsent();
  const viewedItems = useRef<Set<string>>(new Set());
  const clickedItems = useRef<Set<string>>(new Set());
  const viewUuids = useRef<Map<string, string>>(new Map());

  const handleItemVisible: OnItemVisible = useCallback(
    (payload) => {
      if (!isSnowplowReady || viewedItems.current.has(payload.article.id))
        return;

      viewedItems.current.add(payload.article.id);
      const viewUuid = randomUUID();
      viewUuids.current.set(payload.article.id, viewUuid);

      const recommendationData =
        consent?.development || consent?.personalization
          ? payload.meta?.recommendationData
          : undefined;

      trackVideoFeedItemView({
        articleId: payload.article.id,
        viewUuid,
        itemIndex: payload.index,
        contentIndex,
        label: title,
        recommendationId: recommendationData?.recommendationUuid,
        recommendationMethod: recommendationData?.recommendationMethod,
        abTestName: recommendationData?.abTestName,
        abTestGroup:
          recommendationData?.abTestVariant !== undefined
            ? String(recommendationData.abTestVariant)
            : undefined,
      });
    },
    [
      isSnowplowReady,
      consent?.development,
      consent?.personalization,
      contentIndex,
      title,
    ]
  );

  const handleItemClick: OnItemClick = useCallback(
    (payload) => {
      const viewUuid = viewUuids.current.get(payload.article.id);
      if (
        !isSnowplowReady ||
        !viewUuid ||
        clickedItems.current.has(payload.article.id)
      )
        return;

      clickedItems.current.add(payload.article.id);
      trackClick({ action: 'link', viewUuid });
    },
    [isSnowplowReady]
  );

  return { handleItemVisible, handleItemClick };
};
